import { I18nLang } from '../models/user';

/**
 * Primary language mapping using I18nLang enum
 * Maps core language IDs to their corresponding country codes for flag display
 */
export const I18N_LANG_TO_COUNTRY: Record<I18nLang, string> = {
  [I18nLang.EN]: 'GB', // English
  [I18nLang.FR]: 'FR', // French
  [I18nLang.DE]: 'DE', // German
  [I18nLang.IT]: 'IT', // Italian
  [I18nLang.ES]: 'ES', // Spanish
  [I18nLang.NL]: 'NL', // Dutch
  [I18nLang.PL]: 'PL', // Polish
  [I18nLang.PT]: 'PT', // Portuguese
};

/**
 * Extended language ID to Country Code mapping
 * Includes additional languages beyond the core I18nLang enum
 */
export const EXTENDED_LANG_TO_COUNTRY: Record<number, string> = {
  ...I18N_LANG_TO_COUNTRY,
  8: 'RU', // Russian
  9: 'CZ', // Czech
  10: 'SK', // Slovak
  11: 'HU', // Hungarian
  12: 'RO', // Romanian
  13: 'BG', // Bulgarian
  14: 'HR', // Croatian
  15: 'SI', // Slovenian
  16: 'SE', // Swedish
  17: 'NO', // Norwegian
  18: 'DK', // Danish
  19: 'FI', // Finnish
  20: 'GR', // Greek
  21: 'TR', // Turkish
};

/**
 * Country code to flag emoji mapping
 * Maps country codes to their corresponding flag emojis
 */
export const COUNTRY_TO_FLAG: Record<string, string> = {
  'AD': '🇦🇩', // Andorra
  'AL': '🇦🇱', // Albania
  'AM': '🇦🇲', // Armenia
  'AT': '🇦🇹', // Austria
  'AZ': '🇦🇿', // Azerbaijan
  'BA': '🇧🇦', // Bosnia and Herzegovina
  'BE': '🇧🇪', // Belgium
  'BG': '🇧🇬', // Bulgaria
  'BY': '🇧🇾', // Belarus
  'CH': '🇨🇭', // Switzerland
  'CY': '🇨🇾', // Cyprus
  'CZ': '🇨🇿', // Czech Republic
  'DE': '🇩🇪', // Germany
  'DK': '🇩🇰', // Denmark
  'EE': '🇪🇪', // Estonia
  'ES': '🇪🇸', // Spain
  'FI': '🇫🇮', // Finland
  'FR': '🇫🇷', // France
  'GB': '🇬🇧', // United Kingdom
  'GE': '🇬🇪', // Georgia
  'GR': '🇬🇷', // Greece
  'HR': '🇭🇷', // Croatia
  'HU': '🇭🇺', // Hungary
  'IE': '🇮🇪', // Ireland
  'IS': '🇮🇸', // Iceland
  'IT': '🇮🇹', // Italy
  'KZ': '🇰🇿', // Kazakhstan
  'KG': '🇰🇬', // Kyrgyzstan
  'LI': '🇱🇮', // Liechtenstein
  'LT': '🇱🇹', // Lithuania
  'LU': '🇱🇺', // Luxembourg
  'LV': '🇱🇻', // Latvia
  'MD': '🇲🇩', // Moldova
  'MC': '🇲🇨', // Monaco
  'ME': '🇲🇪', // Montenegro
  'MK': '🇲🇰', // North Macedonia
  'MT': '🇲🇹', // Malta
  'NL': '🇳🇱', // Netherlands
  'NO': '🇳🇴', // Norway
  'PL': '🇵🇱', // Poland
  'PT': '🇵🇹', // Portugal
  'RO': '🇷🇴', // Romania
  'RS': '🇷🇸', // Serbia
  'RU': '🇷🇺', // Russia
  'SE': '🇸🇪', // Sweden
  'SI': '🇸🇮', // Slovenia
  'SM': '🇸🇲', // San Marino
  'SK': '🇸🇰', // Slovakia
  'TJ': '🇹🇯', // Tajikistan
  'TN': '🇹🇳', // Tunisia
  'TR': '🇹🇷', // Turkey
  'UA': '🇺🇦', // Ukraine
  'US': '🇺🇸', // United States
  'UZ': '🇺🇿', // Uzbekistan
  'VA': '🇻🇦', // Vatican City
  'XK': '🇽🇰', // Kosovo
};

/**
 * Get flag emoji for a language ID
 * @param langId - The language ID number
 * @returns The flag emoji string or fallback text
 */
export function getLanguageFlag(langId: number): string {
  const countryCode = EXTENDED_LANG_TO_COUNTRY[langId];
  if (countryCode !== undefined) {
    return COUNTRY_TO_FLAG[countryCode] || `Lang ${langId}`;
  }
  return `Lang ${langId}`;
}

/**
 * Get country code for a language ID
 * @param langId - The language ID number
 * @returns The country code or null if not found
 */
export function getLanguageCountryCode(langId: number): string | null {
  return EXTENDED_LANG_TO_COUNTRY[langId] || null;
}